import React, { useContext, useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { Eye, Loader2, Printer, UserCog, Users } from "lucide-react";
import { base_url } from "../../config";
import axios from "axios";
import RegistrationContext from "../../contexts/RegistrationContext";
import RegistrationDetail from "./RegistrationDetail";
import BadgeControl from "./BadgeControl";

const statusFilters = [
  { label: "All", value: "" },
  { label: "Completed", value: "Completed" },
  { label: "In progress", value: "In progress" },
];

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
// Everything the alphabet doesn't cover goes here: a name in another script, a family name
// that starts with a digit or an apostrophe, or a row with the column empty.
const OTHER_INITIALS = "#";

/**
 * The letter a registration files under.
 *
 * Accents are folded first, so Álvarez is under A and Öztürk under O — the desk looking a
 * delegate up thinks in plain letters, and splitting Ö out would hide people under a chip
 * nobody clicks.
 */
function familyInitial(row) {
  // The API sends family_name; the fallback is for a page still talking to a backend that
  // predates it, where the last word of the full name is the best guess available.
  const family = String(row.family_name || "").trim()
    || String(row.name || "").trim().split(/\s+/).pop()
    || "";
  const first = family
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .charAt(0)
    .toUpperCase();

  return /[A-Z]/.test(first) ? first : OTHER_INITIALS;
}

function toDateAssumingUTC(input) {
  if (input instanceof Date) return input;
  if (typeof input === "number") return new Date(input);
  const s = String(input).trim();
  if (/Z$|[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  const isoish = s.replace(" ", "T");
  return new Date(isoish + "Z");
}

// Shared dark MRT sx — applied at the cell/toolbar level so it doesn't bleed
// into the rest of the app's MUI surfaces.
const darkPaperSx = {
  backgroundColor: "rgba(30, 41, 59, 0.5)",
  backgroundImage: "none",
  color: "#e2e8f0",
  border: "1px solid rgba(148,163,184,0.16)",
  borderRadius: "1rem",
  boxShadow: "none",
};
const darkToolbarSx = {
  backgroundColor: "transparent",
  color: "#e2e8f0",
  borderBottom: "1px solid rgba(148,163,184,0.08)",
  "& .MuiButtonBase-root": { color: "#cbd5e1" },
  "& .MuiInputBase-root": { color: "#e2e8f0" },
  "& .MuiInput-underline:before": { borderBottomColor: "rgba(148,163,184,0.2)" },
  "& .MuiSvgIcon-root": { color: "#94a3b8" },
};
const darkHeadCellSx = {
  padding: "10px 8px",
  backgroundColor: "rgba(15, 23, 42, 0.6)",
  color: "#cbd5e1",
  borderBottom: "1px solid rgba(148,163,184,0.16)",
  fontWeight: 600,
  fontSize: "0.7rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  "& .MuiBox-root": { color: "#cbd5e1" },
  "& .MuiSvgIcon-root": { color: "#94a3b8" },
};
const darkBodyCellSx = {
  padding: "8px 8px",
  color: "#e2e8f0",
  fontSize: "0.8rem",
  borderBottom: "1px solid rgba(148,163,184,0.08)",
};
const darkRowSx = {
  backgroundColor: "transparent",
  "&:hover td": { backgroundColor: "rgba(148,163,184,0.06) !important" },
};

const UserTable = () => {
  const [data, setData] = useState([]);
  const [typeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Completed");
  const [letterFilter, setLetterFilter] = useState("");

  const { reload, setReload, permissions } = useContext(RegistrationContext);
  const [openModal, setOpenModal] = useState(false);
  const [openModalBadges, setOpenModalBadges] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [userData, setUserData] = useState(null);
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const [printNotice, setPrintNotice] = useState("");

  useEffect(() => {
    axios
      .get(`${base_url}/api/admin/registrations`)
      .then((response) => setData(response.data))
      .catch((error) => console.error("Error fetching registrations:", error));
  }, []);

  // Everything except the letter, so the A–Z bar can be counted against the status filter
  // in force rather than against the whole table.
  const statusFiltered = useMemo(
    () =>
      data.filter((row) => {
        const typeMatch = typeFilter ? row.type === typeFilter : true;
        const statusMatch = statusFilter
          ? statusFilter === "Completed"
            ? row.status === "Completed"
            : ["In progress", "in-progress", "progress"].includes(row.status)
          : true;
        return typeMatch && statusMatch;
      }),
    [data, typeFilter, statusFilter]
  );

  const letterCounts = useMemo(() => {
    const counts = {};
    statusFiltered.forEach((row) => {
      const initial = familyInitial(row);
      counts[initial] = (counts[initial] || 0) + 1;
    });
    return counts;
  }, [statusFiltered]);

  const filteredData = useMemo(
    () =>
      letterFilter
        ? statusFiltered.filter((row) => familyInitial(row) === letterFilter)
        : statusFiltered,
    [statusFiltered, letterFilter]
  );

  const refresh = () => {
    axios
      .get(`${base_url}/api/admin/registrations`)
      .then((response) => setData(response.data))
      .catch((error) => console.error("Error fetching data:", error));
  };

  const typeChip = (value) => {
    switch (value) {
      case "industry":      return { label: "Industry",      cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
      case "regular":       return { label: "Regular",       cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" };
      case "student":       return { label: "Student",       cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
      case "student-paper": return { label: "Student paper", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" };
      case "exhibitor":     return { label: "Exhibitor",     cls: "bg-orange-500/15 text-orange-300 border-orange-500/30" };
      default:              return { label: value,           cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" };
    }
  };

  const accessChip = (value) => {
    switch (value) {
      case "full":      return { label: "Full",      cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" };
      case "workshops": return { label: "Workshops", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
      default:          return { label: value,       cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" };
    }
  };

  const columns = [
    {
      accessorKey: "status",
      header: "",
      enableColumnActions: false,
      enableSorting: false,
      size: 32,
      maxSize: 32,
      minSize: 32,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (value === "Completed") {
          return (
            <span className="flex items-center justify-center">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-emerald-500/30 inline-block" />
            </span>
          );
        }
        if (["In progress", "in-progress", "progress"].includes(value)) {
          return (
            <span className="flex items-center justify-center">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-amber-500/30 inline-block" />
            </span>
          );
        }
        return (
          <span className="flex items-center justify-center">
            <span className="h-2.5 w-2.5 rounded-full bg-slate-500 inline-block" />
          </span>
        );
      },
    },
    {
      accessorKey: "code",
      header: "Code",
      size: 92,
      maxSize: 92,
      minSize: 92,
      Cell: ({ cell }) => (
        <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-200 border border-slate-600/40">
          {cell.getValue()}
        </span>
      ),
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 110,
      maxSize: 110,
      minSize: 110,
      Cell: ({ cell }) => {
        const { label, cls } = typeChip(cell.getValue());
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}>
            {label}
          </span>
        );
      },
    },
    {
      accessorKey: "access",
      header: "Access",
      size: 100,
      maxSize: 100,
      minSize: 80,
      Cell: ({ cell, row }) => {
        if (row.original.type === "industry") {
          return <span className="text-slate-500">—</span>;
        }
        const { label, cls } = accessChip(cell.getValue());
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}>
            {label}
          </span>
        );
      },
    },
    {
      header: "Name & Email",
      accessorFn: (row) => `${row.name ?? ""} ${row.email ?? ""}`,
      Cell: ({ row }) => (
        <div className="leading-tight">
          <div className="text-slate-100">{row.original.name}</div>
          <div className="text-[11px] text-slate-400">{row.original.email}</div>
        </div>
      ),
    },
    {
      id: "most_recent_update",
      header: "Last update",
      accessorKey: "most_recent_update",
      size: 150,
      Cell: ({ cell }) => {
        const raw = cell.getValue();
        if (!raw) return <span className="text-slate-500">—</span>;
        const date = toDateAssumingUTC(raw);
        return (
          <span className="text-slate-300 text-[11px]">
            {date.toLocaleString(undefined, {
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      enableColumnActions: false,
      size: 100,
      maxSize: 100,
      minSize: 100,
      Cell: ({ row }) => (
        <div className="flex items-center gap-1.5 justify-end">
          <button
            onClick={() => loadUserData(row.original)}
            title="View details"
            className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 hover:text-blue-200 transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          {permissions.includes("ijcai_impersonate") && (
            <button
              onClick={() => handleImpersonate(row.original)}
              title="Impersonate"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-slate-600/30 text-slate-300 hover:bg-slate-600/50 hover:text-slate-100 border border-slate-600/40 transition-colors"
            >
              <UserCog className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleImpersonate = (row) => {
    axios
      .post(`${base_url}/api/admin/impersonate`, { id: row.code })
      .then(() => setReload(reload + 1))
      .catch((error) => console.error("Error impersonating:", error));
  };

  /**
   * Badges still to print: completed registrations whose badge_printed is not set.
   *
   * Deliberately counted over all the data rather than the filtered rows — the status
   * filter can select "In progress", and those have no badge coming.
   */
  const unprintedBadges = data.filter(
    (row) => row.status === "Completed" && !row.badge_printed
  ).length;

  /**
   * Print every unprinted badge. The server bounds each response and reports what is left,
   * so ask again until nothing remains rather than rendering hundreds in one request.
   */
  const handleBulkPrintBadges = async () => {
    if (bulkPrinting) return;
    setBulkPrinting(true);
    setPrintNotice("");
    let part = 0;
    let printed = 0;
    try {
      for (;;) {
        part += 1;
        const response = await axios.post(
          `${base_url}/api/admin/print_registration_badges`,
          { only_unprinted: true },
          { responseType: "blob" }
        );
        // An empty batch comes back as JSON rather than a PDF.
        if (response.data.type === "application/json") break;

        const count = Number(response.headers["x-printed"] || 0);
        const remaining = Number(response.headers["x-remaining"] || 0);
        printed += count;

        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `badges_${String(part).padStart(2, "0")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        if (!remaining || !count) break;
      }
      setPrintNotice(`${printed} badge(s) printed in ${part} file(s) and marked as printed.`);
      const refreshed = await axios.get(`${base_url}/api/admin/registrations`);
      setData(refreshed.data);
    } catch (error) {
      console.error("Error printing badges:", error);
      setPrintNotice("Printing stopped early — see the console.");
    } finally {
      setBulkPrinting(false);
    }
  };

  const loadUserData = async (row) => {
    try {
      setOpenModal(true);
      setLoadingData(true);
      const data = await axios.post(`${base_url}/api/admin/getuser`, { id: row.code });
      setUserData(data.data);
      setLoadingData(false);
    } catch (error) {
      console.error("Error getting user", error);
      setOpenModal(false);
    }
  };

  const filterChipClass = (filter, value) => {
    const isActive = statusFilter === value;
    if (!isActive) {
      return "px-2.5 py-1 rounded-md text-xs font-medium border border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-800 hover:text-slate-100 transition-colors";
    }
    if (value === "Completed") {
      return "px-2.5 py-1 rounded-md text-xs font-semibold border bg-emerald-500/20 text-emerald-200 border-emerald-500/40";
    }
    if (value === "In progress") {
      return "px-2.5 py-1 rounded-md text-xs font-semibold border bg-amber-500/20 text-amber-200 border-amber-500/40";
    }
    return "px-2.5 py-1 rounded-md text-xs font-semibold border bg-blue-500/20 text-blue-200 border-blue-500/40";
  };

  /** A letter with nobody behind it is shown greyed rather than hidden — the gap is the answer. */
  const letterChipClass = (value, count) => {
    const base =
      "min-w-[1.75rem] px-1.5 py-1 rounded-md text-xs font-medium border text-center transition-colors";
    if (letterFilter === value) {
      return `${base} font-semibold bg-blue-500/20 text-blue-200 border-blue-500/40`;
    }
    if (!count) {
      return `${base} border-slate-800 bg-slate-900/40 text-slate-600 cursor-default`;
    }
    return `${base} border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-800 hover:text-slate-100`;
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1500px] px-4 py-5 space-y-4">
        <header className="flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-400" />
            Registrations
            <span className="text-xs font-normal text-slate-400">
              ({filteredData.length} of {data.length})
            </span>
          </h1>
          <button
            onClick={() => {
              if (
                window.confirm(
                  `This builds a PDF of the ${unprintedBadges} completed registration(s) whose badge has not been printed, two to a sheet, and marks them as printed. Page filters do not apply. Large batches arrive as several files. Continue?`
                )
              ) {
                handleBulkPrintBadges();
              }
            }}
            disabled={bulkPrinting || !unprintedBadges}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
          >
            {bulkPrinting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Printer className="h-3.5 w-3.5" />
            )}
            Print {unprintedBadges} badges unprinted
          </button>
        </header>

        {printNotice && (
          <div className="rounded-lg border border-slate-700/50 bg-slate-800/50 px-3 py-2 text-xs text-slate-300">
            {printNotice}
          </div>
        )}

        {/* Filter bar */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-slate-400 mr-1">
            Status
          </span>
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={filterChipClass(filter, filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Family name initial */}
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-slate-400 mr-1">
            Family name
          </span>
          <button
            onClick={() => setLetterFilter("")}
            className={letterChipClass("", 1)}
            title={`${statusFiltered.length} registration(s)`}
          >
            All
          </button>
          {alphabet.concat(OTHER_INITIALS).map((letter) => {
            const count = letterCounts[letter] || 0;
            return (
              <button
                key={letter}
                onClick={() => setLetterFilter(letter)}
                disabled={!count}
                className={letterChipClass(letter, count)}
                title={
                  letter === OTHER_INITIALS
                    ? `${count} registration(s) whose family name starts with something other than A–Z`
                    : `${count} registration(s)`
                }
              >
                {letter}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="rounded-2xl overflow-hidden">
          <MaterialReactTable
            columns={columns}
            data={filteredData}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            initialState={{ density: "compact", pagination: { pageSize: 25, pageIndex: 0 } }}
            muiTablePaperProps={{ sx: darkPaperSx }}
            muiTableContainerProps={{ sx: { backgroundColor: "transparent" } }}
            muiTopToolbarProps={{ sx: darkToolbarSx }}
            muiBottomToolbarProps={{ sx: darkToolbarSx }}
            muiTableHeadCellProps={{ sx: darkHeadCellSx }}
            muiTableBodyCellProps={{ sx: darkBodyCellSx }}
            muiTableBodyRowProps={{ sx: darkRowSx }}
            muiSearchTextFieldProps={{
              placeholder: "Search…",
              sx: {
                "& .MuiInputBase-input": { color: "#e2e8f0" },
                "& .MuiInputBase-input::placeholder": { color: "#64748b", opacity: 1 },
              },
            }}
          />
        </div>

        <RegistrationDetail
          open={openModal}
          loading={loadingData}
          data={userData}
          onClose={() => setOpenModal(false)}
        />
        <BadgeControl
          open={openModalBadges}
          loading={loadingData}
          data={userData}
          onClose={() => setOpenModalBadges(false)}
          onUpdate={refresh}
        />
      </div>
    </div>
  );
};

export default UserTable;
